import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { useReferral } from "@/context/ReferralContext";
import { track } from "@/lib/analytics";
import { ArrowRight, CheckCircle2, Instagram, Youtube, Linkedin, AtSign } from "lucide-react";

const PLATFORM_ICON = {
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  tiktok: AtSign,
  other: AtSign,
};

export default function InfluencerLanding() {
  const { slug } = useParams();
  const { attach, referralCode } = useReferral();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/influencer-landing/${slug}`)
      .then((r) => {
        setData(r.data);
        track("referral_landing_view", { landing_slug: slug });
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto py-24 px-6 text-center">
          <h1 className="text-3xl font-bold text-[#0B0B0B]">Landing no encontrada</h1>
          <p className="mt-2 text-gray-600">Este perfil no está disponible o fue desactivado.</p>
          <Link to="/" className="mt-6 inline-block text-[#F26A21] hover:underline">Volver al inicio</Link>
        </div>
      </PublicLayout>
    );
  }
  if (!data) {
    return <PublicLayout><div className="py-24 text-center text-gray-500">Cargando…</div></PublicLayout>;
  }

  const Icon = PLATFORM_ICON[data.platform] || AtSign;
  const attachAndGo = async (href) => {
    if (data.primary_code) {
      setBusy(true);
      await attach(data.primary_code);
      setBusy(false);
    }
    if (href) window.location.href = href;
  };

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="bg-[#0B0B0B] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-4 text-center lg:text-left">
              <div className="w-32 h-32 mx-auto lg:mx-0 rounded-2xl bg-[#F26A21] flex items-center justify-center text-5xl font-extrabold text-[#0B0B0B]">
                {data.photo_url
                  ? <img src={data.photo_url} alt={data.name} className="w-full h-full object-cover rounded-2xl" />
                  : (data.name || "?").slice(0, 2).toUpperCase()}
              </div>
              <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs">
                <Icon className="w-3.5 h-3.5 text-[#F26A21]" aria-hidden="true" />
                <span className="font-semibold">{data.handle}</span>
                <span className="text-gray-400">· {data.platform}</span>
              </div>
            </div>
            <div className="lg:col-span-8">
              <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">
                Recomendación de {data.name.split(" ")[0]}
              </p>
              <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.1] tracking-tight font-display">
                {data.name}
              </h1>
              {data.hero_copy && (
                <p className="mt-4 text-lg text-gray-300 max-w-2xl leading-relaxed">
                  "{data.hero_copy}"
                </p>
              )}
              {data.primary_code && (
                <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-md bg-[#F26A21]/15 border border-[#F26A21]/50">
                  <CheckCircle2 className="w-4 h-4 text-[#F26A21]" aria-hidden="true" />
                  <span className="text-sm">
                    Código activo:
                    <span className="ml-2 font-mono font-bold text-[#F26A21]" data-testid="influencer-code">
                      {data.primary_code}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Featured programs */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-widest text-[#F26A21] font-bold">Mis recomendados</p>
        <h2 className="mt-2 text-3xl font-bold text-[#0B0B0B] font-display">
          {data.featured_programs.length > 0
            ? `${data.featured_programs.length} programa${data.featured_programs.length > 1 ? "s" : ""} que uso con equipos reales`
            : "Programas destacados"}
        </h2>

        {data.featured_programs.length === 0 ? (
          <div className="mt-6 p-8 rounded-2xl border-2 border-dashed border-gray-300 text-center bg-[#F9FAFB]">
            <p className="text-sm text-gray-600">
              Aún no hay programas destacados. Explora todo el catálogo DCC:
            </p>
            <button
              onClick={() => attachAndGo("/catalogo")}
              data-testid="landing-explore-catalog"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#F26A21] hover:bg-[#D9541A] text-[#0B0B0B] font-semibold text-sm transition-all hover:scale-[1.02]"
            >
              Explora los 193 programas <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.featured_programs.map((p) => (
              <article
                key={p.id}
                data-testid={`landing-program-${p.code}`}
                className="p-5 bg-white border border-gray-200 rounded-2xl hover:border-[#F26A21] hover:shadow-lg transition-all"
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#F26A21]">
                  {p.code}
                </p>
                <h3 className="mt-2 text-lg font-bold text-[#0B0B0B] leading-tight">{p.title}</h3>
                <p className="mt-2 text-xs text-gray-600">
                  {p.format} · {p.duration_text} · {p.modality}
                </p>
                {p.price_mxn && (
                  <p className="mt-3 text-2xl font-extrabold text-[#0B0B0B]">
                    ${p.price_mxn.toLocaleString()} <span className="text-xs font-semibold text-gray-600">MXN + IVA</span>
                  </p>
                )}
                {p.has_iada && (
                  <p className="mt-1 text-[11px] text-[#F26A21] font-semibold">+ Aval IADA disponible</p>
                )}
                <button
                  onClick={() => attachAndGo(`/catalogo/${p.slug}?ref=${encodeURIComponent(data.primary_code || "")}`)}
                  disabled={busy}
                  data-testid={`landing-cta-${p.code}`}
                  className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0B0B0B] hover:bg-black text-white text-sm font-semibold transition-all hover:scale-[1.02]"
                >
                  Ver programa <ArrowRight className="w-3 h-3" />
                </button>
              </article>
            ))}
          </div>
        )}

        {referralCode === data.primary_code && (
          <p className="mt-8 text-xs text-emerald-700 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 inline-block">
            ✓ Tu compra con el código {data.primary_code} contribuye al programa de partnership DCC × {data.name.split(" ")[0]}.
          </p>
        )}
      </section>
    </PublicLayout>
  );
}
